import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface SiteContentRow {
  key: string;
  section: string;
  label: string;
  kind: "text" | "textarea" | "image" | "richtext";
  value_en: string | null;
  value_ar: string | null;
  image_url: string | null;
  sort_order: number;
}

let cache: Record<string, SiteContentRow> | null = null;
let inflight: Promise<Record<string, SiteContentRow>> | null = null;
const listeners = new Set<() => void>();

const load = async () => {
  if (cache) return cache;
  if (inflight) return inflight;
  inflight = (async () => {
    const { data } = await supabase.from("site_content" as any).select("*");
    const map: Record<string, SiteContentRow> = {};
    (data as any[] | null)?.forEach((r) => (map[r.key] = r as SiteContentRow));
    cache = map;
    inflight = null;
    listeners.forEach((l) => l());
    return map;
  })();
  return inflight;
};

export const invalidateSiteContent = () => {
  cache = null;
  inflight = null;
  load();
};

export const useSiteContent = () => {
  const [, setTick] = useState(0);
  const [ready, setReady] = useState(!!cache);

  useEffect(() => {
    const listener = () => {
      setTick((t) => t + 1);
      setReady(true);
    };
    listeners.add(listener);
    if (!cache) {
      load().then(() => listener());
    } else {
      setReady(true);
    }
    return () => {
      listeners.delete(listener);
    };
  }, []);

  const get = (key: string) => cache?.[key];

  const text = (key: string, isAr: boolean, fallback = ""): string => {
    const row = get(key);
    if (!row) return fallback;
    const v = (isAr ? row.value_ar : row.value_en) || "";
    return v.trim() ? v : fallback;
  };

  const image = (key: string, fallback: string): string => {
    const row = get(key);
    const v = row?.image_url || "";
    if (!v.trim() || v.startsWith("/src/")) return fallback;
    return v;
  };

  return { ready, text, image, get };
};
